import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { apiService } from '../services/api';

const AuthContext = createContext();

// Session timeout: 10 minutes of inactivity
const SESSION_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const lastActivityTimeRef = useRef(Date.now());
  const timeoutIntervalRef = useRef(null);

  // Update last activity time
  const updateActivity = useCallback(() => {
    lastActivityTimeRef.current = Date.now();
  }, []);

  // Session timeout check effect
  useEffect(() => {
    if (!token) return;

    // Clear any existing interval
    if (timeoutIntervalRef.current) {
      clearInterval(timeoutIntervalRef.current);
    }

    // Check every 30 seconds if session has timed out
    timeoutIntervalRef.current = setInterval(() => {
      const currentTime = Date.now();
      const timeSinceLastActivity = currentTime - lastActivityTimeRef.current;

      if (timeSinceLastActivity >= SESSION_TIMEOUT) {
        // Session timed out - logout
        logout();
      }
    }, 30000); // Check every 30 seconds

    return () => {
      if (timeoutIntervalRef.current) {
        clearInterval(timeoutIntervalRef.current);
      }
    };
  }, [token]);

  useEffect(() => {
    if (token) {
      // Verify token and get user info
      apiService.getCurrentAdmin()
        .then((response) => {
          setUser(response.data);
          // Reset activity time on successful auth
          lastActivityTimeRef.current = Date.now();
        })
        .catch(() => {
          // Token invalid, clear it
          localStorage.removeItem('admin_token');
          setToken(null);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (credentials) => {
    const response = await apiService.login(credentials);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('admin_token', access_token);
    setToken(access_token);
    setUser(userData);
    // Reset activity timer on login
    lastActivityTimeRef.current = Date.now();
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setUser(null);
    if (timeoutIntervalRef.current) {
      clearInterval(timeoutIntervalRef.current);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, isAuthenticated: !!user, isSuperAdmin: user?.role === 'super_admin', isClubAdmin: user?.role === 'club_admin', updateActivity }}>
      {children}
    </AuthContext.Provider>
  );
};